import { useState } from 'react';
import { Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { CachedImage, DotMenu, DoubleCheckModal } from '@shared/ui';
import { useCreateHomeTile, HomeTileType } from '@modules/home-tiles';
import { useDeleteFilterSet } from '@modules/filter-sets';
import { useDeleteCustomSet } from '@modules/custom-sets';
import { AttachLinkToBannerModal } from '@modules/banners';
import './SetCard.css';

interface SetCardProps {
  title: string;
  description?: string;
  imageUrl?: string;
  path?: string;
  onClick?: () => void;
  tileType?: HomeTileType;
  setId?: number;
  setType?: 'filter' | 'custom';
  onEdit?: () => void;
}

export default function SetCard({ title, description, imageUrl, path, onClick, tileType, setId, setType, onEdit }: SetCardProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAttachLinkModalOpen, setIsAttachLinkModalOpen] = useState(false);
  const createHomeTile = useCreateHomeTile();
  const deleteFilterSet = useDeleteFilterSet();
  const deleteCustomSet = useDeleteCustomSet();

  const handleAddToHome = async () => {
    if (!path || !tileType) {
      alert('Невозможно добавить на главную: не указан путь или тип');
      return;
    }

    await createHomeTile.mutateAsync({
      type: tileType,
      name: title,
      path: path,
      img: imageUrl,
    });
  };

  const handleAttachLinkToBanner = () => {
    setIsAttachLinkModalOpen(true);
  };

  const handleDelete = () => {
    if (!setId || !setType) {
      return;
    }
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!setId || !setType) {
      return;
    }

    try {
      if (setType === 'filter') {
        await deleteFilterSet.mutateAsync(setId);
      } else if (setType === 'custom') {
        await deleteCustomSet.mutateAsync(setId);
      }

      notifications.show({
        title: 'Успешно',
        message: 'Подборка удалена',
        color: 'green',
      });

      setIsDeleteModalOpen(false);
    } catch (error: unknown) {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось удалить подборку',
        color: 'red',
      });
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const menuItems = [
    {
      label: 'Добавить на главную',
      onClick: handleAddToHome,
    },
    ...(path ? [{
      label: 'Прикрепить ссылку к баннеру',
      onClick: handleAttachLinkToBanner,
    }] : []),
    ...(onEdit ? [{
      label: 'Редактировать',
      onClick: onEdit,
    }] : []),
    ...(setId && setType ? [{
      label: 'Удалить',
      onClick: handleDelete,
      isDanger: true,
    }] : []),
  ];

  const content = (
    <>
      {imageUrl ? (
        <div className="set-card-image">
          <CachedImage src={imageUrl} alt={title} />
        </div>
      ) : (
        <div className="set-card-placeholder">
          <span>📦</span>
        </div>
      )}
      <div className="set-card-content">
        <h3 className="set-card-title">{title}</h3>
        {description && (
          <p className="set-card-description">{description}</p>
        )}
      </div>
    </>
  );

  if (path) {
    return (
      <>
        <div className="set-card-wrapper">
          <Link to={path} className="set-card">
            {content}
          </Link>
          {(tileType || (setId && setType) || onEdit || path) && (
            <div className="set-card-menu" onClick={(e) => e.stopPropagation()}>
              <DotMenu items={menuItems} inverted />
            </div>
          )}
        </div>
        {isDeleteModalOpen && <DoubleCheckModal
          isOpen={isDeleteModalOpen}
          onClose={cancelDelete}
          title="Удалить подборку?"
          message={`Вы уверены, что хотите удалить подборку "${title}"?`}
          secondButtonText="Удалить"
          secondButtonAction={confirmDelete}
          isLoading={deleteFilterSet.isPending}
        />}
        {path && isAttachLinkModalOpen && (
          <AttachLinkToBannerModal
            isOpen={isAttachLinkModalOpen}
            onClose={() => setIsAttachLinkModalOpen(false)}
            link={path}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="set-card-wrapper">
        <div className="set-card" onClick={onClick}>
          {content}
        </div>
        {(tileType || (setId && setType) || onEdit || path) && (
          <div className="set-card-menu" onClick={(e) => e.stopPropagation()}>
            <DotMenu items={menuItems} />
          </div>
        )}
      </div>
      {isDeleteModalOpen && <DoubleCheckModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        title="Удалить подборку?"
        message={`Вы уверены, что хотите удалить подборку "${title}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deleteFilterSet.isPending}
      />}
      {path && isAttachLinkModalOpen && (
        <AttachLinkToBannerModal
          isOpen={isAttachLinkModalOpen}
          onClose={() => setIsAttachLinkModalOpen(false)}
          link={path}
        />
      )}
    </>
  );
}

